import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { Link } from "react-router-dom";

import { IconSearch } from "@/components/Icons";

import styles from "./kit.module.css";

/** Joins class names, dropping anything falsy. Saves every component below
 * repeating the same ternary. */
function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/* -------------------------------------------------------------- buttons -- */

type Tone = "primary" | "default" | "subtle" | "danger";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: Tone;
  size?: "md" | "lg";
  /** Renders square, for a button whose whole content is an icon. Give it an
   * `aria-label` — there is no text to read. */
  icon?: boolean;
};

/**
 * A link that looks like a button.
 *
 * Here rather than in each screen because every screen needed it and each one
 * improvised: a ``<Link>`` dropped inside a ``<Button>`` keeps the link's own
 * colour, so a primary action rendered blue text on a blue fill and read as an
 * empty rectangle. One component, one answer.
 */
export function LinkButton({
  to,
  tone = "default",
  size = "md",
  children,
}: {
  to: string;
  tone?: Tone;
  size?: "md" | "lg";
  children: ReactNode;
}) {
  return (
    <Link to={to} className={cx(styles.btn, styles[tone], size === "lg" && styles.lg)}>
      {children}
    </Link>
  );
}

export function Button({
  tone = "default",
  size = "md",
  icon = false,
  className,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        styles.btn,
        styles[tone],
        size === "lg" && styles.lg,
        icon && styles.iconOnly,
        className,
      )}
      {...rest}
    />
  );
}

/* --------------------------------------------------------------- inputs -- */

export function Input({
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(styles.control, className)} {...rest} />;
}

export function Textarea({
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(styles.control, className)} {...rest} />;
}

export function Select({
  className,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx(styles.control, className)} {...rest} />;
}

/** A search box with its icon inside. Recognisable at a glance in a toolbar
 * that is otherwise a row of identical rectangles. */
export function SearchField({
  className,
  placeholder = "Search",
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={cx(styles.search, className)}>
      <span className={styles.searchIcon}>
        <IconSearch size={14} />
      </span>
      <Input type="search" placeholder={placeholder} {...rest} />
    </div>
  );
}

/* ---------------------------------------------------------------- field -- */

/**
 * A labelled control.
 *
 * Hint and error share one slot: an error replaces the hint rather than
 * appearing under it, so a field never grows and pushes the rest of the form
 * down as you type.
 */
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.field}>
      <label className={cx(styles.label, required && styles.required)} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <span className={styles.fieldError}>{error}</span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}

/** Fields side by side, stacking when there is no room. No breakpoints to
 * think about at the call site. */
export function FieldRow({ children }: { children: ReactNode }) {
  return <div className={styles.fieldRow}>{children}</div>;
}

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cx(styles.check, className)}>
      <input type="checkbox" {...rest} />
      {label}
    </label>
  );
}

/**
 * A checkbox standing in a row of labelled fields.
 *
 * A bare checkbox has nothing to align to: the fields beside it are a label above
 * a control, and the checkbox is one short line, so it lands on the *label* line
 * and reads as floating above the inputs.
 *
 * It borrows the field's own structure — an empty label line, then the control
 * slot — so it lines up by construction rather than by an offset somebody measured
 * off a screenshot and that breaks the next time the type scale moves.
 *
 * `label` is the checkbox's own text, beside the box as always. The blank line
 * above it is spacing, which is why it is hidden from assistive tech.
 */
export function CheckboxField({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <div className={cx(styles.field, className)}>
      <span className={styles.label} aria-hidden>
        &nbsp;
      </span>
      <div className={styles.checkLine}>
        <Checkbox label={label} {...rest} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ segmented -- */

/**
 * Two to four options, always visible.
 *
 * Cheaper to read than a dropdown and one keystroke instead of two — which is
 * why the fixture-kind picker and every yes/no in the sport editor uses it.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  name,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  name?: string;
}) {
  return (
    <div className={styles.segmented} role="radiogroup" aria-label={name}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={cx(styles.segment, option.value === value && styles.segmentOn)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** A key, for telling someone which one to press. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className={styles.kbd}>{children}</kbd>;
}
